import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Login from './pages/Login/Login'
import UserDashboard from './pages/UserDashboard/UserDashboard'
import AdminDashboard from './pages/AdminDashboard/AdminDashboard'
import ResetPwd from './pages/ResetPwd/ResetPwd'
import WechatAuth from './pages/WechatAuth/WechatAuth'
import { getLocalStorage } from './utils/localStorageUtil';
import { LOCAL_STORAGE_KEYS } from './const/localStorageConst';
import { PasswordUpdateModalProvider } from './components/PasswordUpdateModalProvider';
import { expirePortalSession, heartbeatPortalSession, isPortalLogoutInProgress, validatePortalSession } from './utils/portalSession';

// 根据本地登录态计算已登录用户应该进入的页面
const getLoggedInRedirectPath = () => {
  const token = getLocalStorage(LOCAL_STORAGE_KEYS.TOKEN) || window.sessionStorage?.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  if (!token) return null;

  const userInfo = getLocalStorage(LOCAL_STORAGE_KEYS.USER_INFO, {});
  const storedIsAdmin = getLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN);
  const isAdmin = storedIsAdmin === true || storedIsAdmin === 'true' || userInfo?.is_admin === true || userInfo?.is_admin === 'true';

  return isAdmin ? '/admin-dashboard' : '/user-dashboard';
};

const hasPortalToken = () => Boolean(getLocalStorage(LOCAL_STORAGE_KEYS.TOKEN));

// 登录页专用路由守卫：已登录时不渲染 Login，直接跳转到对应首页
const LoginRoute = () => {
  const redirectPath = getLoggedInRedirectPath();
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }
  return <Login />;
};

// Only authenticated dashboard routes participate in presence. A hidden tab must not
// count as online; coming back validates the server token before reporting presence.
const TokenCheck = ({ children }) => {
  const location = useLocation();
  const onDashboard = ['/admin-dashboard', '/user-dashboard'].includes(location.pathname);
  const [checking, setChecking] = useState(onDashboard);
  const [validatedPath, setValidatedPath] = useState(null);
  const [networkError, setNetworkError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!onDashboard || !hasPortalToken()) {
      setChecking(false);
      setValidatedPath(null);
      return undefined;
    }
    let active = true;
    let validated = false;
    let validating = false;
    const validate = async () => {
      if (validating || !active || !hasPortalToken() || isPortalLogoutInProgress()) return;
      validating = true;
      const result = await validatePortalSession();
      validating = false;
      if (!active || !hasPortalToken() || isPortalLogoutInProgress()) return;
      if (result?.status === 200) {
        validated = true;
        setNetworkError(false);
        setValidatedPath(location.pathname);
        setChecking(false);
        if (document.visibilityState === 'visible') heartbeatPortalSession();
      } else if (result?.status === 403) {
        expirePortalSession();
      } else {
        validated = false;
        setValidatedPath(null);
        setNetworkError(true);
        setChecking(false);
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') validate();
    };
    const heartbeat = () => {
      if (validated && document.visibilityState === 'visible' && hasPortalToken() && !isPortalLogoutInProgress()) heartbeatPortalSession();
    };
    setChecking(true);
    validate();
    document.addEventListener('visibilitychange', onVisibilityChange);
    const timer = window.setInterval(heartbeat, 60000);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(timer);
    };
  }, [onDashboard, location.pathname, retry]);

  if (onDashboard && hasPortalToken() && (checking || networkError || validatedPath !== location.pathname)) {
    return <div role="status" style={{ padding: 40, textAlign: 'center' }}>
      {networkError ? <>无法验证登录状态，请检查网络后<button onClick={() => setRetry(value => value + 1)}>重试</button></> : '正在验证登录状态…'}
    </div>;
  }
  return children;
};

// 根据用户角色跳转到对应页面
const RoleRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getLocalStorage(LOCAL_STORAGE_KEYS.TOKEN);
  const isAdmin = getLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN);
  const currentPath = location.pathname;

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // 如果是管理员且当前不在管理员页面，跳转到管理员页面
  if (isAdmin && !currentPath.startsWith('/admin')) {
    return <Navigate to="/admin-dashboard" replace />;
  }

  // 如果是普通用户且当前不在用户页面，跳转到用户页面
  if (!isAdmin && !currentPath.startsWith('/user')) {
    return <Navigate to="/user-dashboard" replace />;
  }

  return children;
};

const AppRouter = () => {
  return (
    // 应用部署在 /fuxiaozhu 子路径下时，统一加上 basename
    <BrowserRouter basename="/fuxiaozhu">
      <PasswordUpdateModalProvider>
        <TokenCheck>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/wechatauth" element={<WechatAuth />} />
            <Route path="/reset" element={<ResetPwd />} />
            <Route path="/user-dashboard" element={
              <RoleRoute>
                <UserDashboard />
              </RoleRoute>
            } />
            <Route path="/admin-dashboard" element={
              <RoleRoute>
                <AdminDashboard />
              </RoleRoute>
            } />
          </Routes>
        </TokenCheck>
      </PasswordUpdateModalProvider>
    </BrowserRouter>
  );
};

export default AppRouter;
